import { Component, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { DialogComponentBase } from '@wephone-core-ui';
import { _tk } from 'jsphonelib/translation';
import {
  DialogActionButton,
  Colors,
  PhoneNumberService,
  PhoneNumberValidator,
  EmailValidator,
  NoWhitespaceValidator,
} from '@wephone-utils';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConfigManager } from '@wephone-core/wephone-core.module';
import { CrmContactService, GetContactOpts } from '@wephone-core/service/crm-contact.service';
import { _ti } from '@wephone-translation';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as _ from 'lodash';
import { NoHtmlAndCurlyBracesValidator } from '@wephone-utils/validator/no-html-and-curly-braces-validator';
import { XSSValidator } from '@wephone-utils/validator/xss-validator';

@Component({
  selector: 'app-update-crm-contact-modal',
  templateUrl: './update-crm-contact-modal.component.html',
  styleUrls: ['./update-crm-contact-modal.component.scss']
})
export class UpdateCrmContactModalComponent extends DialogComponentBase implements OnInit {
  dialogTitle = _tk('crm.contact.title.create');
  dialogRightActions: DialogActionButton[] = [
    {
      label: _tk('public.apply'),
      action: () => {
        this.submit();
      },
      visible: () => {
        return true;
      },
      color: Colors.PRIMARY
    }
  ];

  _cancelButton: DialogActionButton = {
    label: _tk('public.button.close'),
    action: () => {
      this.dismiss();
    }
  };

  contactData = {
    crmId: undefined,
    crmContactId: '',
    crmContactType: undefined,
    name: '',
    phoneNumber: '',
    email: '',
    userCallId: undefined,
  };

  isEdit = false;
  form: FormGroup;

  constructor(
    cdr: ChangeDetectorRef,
    public configManager: ConfigManager,
    dialogRef: MatDialogRef<UpdateCrmContactModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly fb: FormBuilder,
    private readonly crmContactService: CrmContactService,
    private readonly phoneNumberService: PhoneNumberService,
  ) {
    super(dialogRef, cdr);
    this.form = this.fb.group({
      name: ['', [Validators.required, NoWhitespaceValidator, NoHtmlAndCurlyBracesValidator(), XSSValidator.sanitize]],
      phoneNumber: ['', [Validators.required, PhoneNumberValidator]],
      email: ['', [NoWhitespaceValidator, EmailValidator]],
    });
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();

    this.contactData.userCallId = this.data.callInfo.userCallId;
    this.contactData.crmId = this.data.callInfo.crmId;
    this.contactData.phoneNumber = this.phoneNumberService.normalizeNumber(this.data.callInfo.phoneNumber);
    this.isEdit = this.data.callInfo.crmId && this.data.callInfo.crmContactId;

    if (this.isEdit) {
      this.dialogTitle = _tk('crm.contact.title.edit');
      const getContactOpts: GetContactOpts = {
        crmId: this.data.callInfo.crmId,
        contactId: this.data.callInfo.crmContactId,
        contactType: this.data.callInfo.crmContactType,
      };
      const crmContact = await this.crmContactService.getContactById(getContactOpts);
      this.contactData.crmContactId = crmContact.crm_contact_id;
      this.contactData.crmContactType = crmContact.type;
      this.contactData.name = [crmContact.first_name, crmContact.last_name].filter(i => !!i).join(' ');
      this.contactData.phoneNumber = crmContact.phone_number;
      this.contactData.email = crmContact.email;
    }

    this.form.patchValue({
      name: this.contactData.name,
      phoneNumber: this.contactData.phoneNumber,
      email: this.contactData.email,
    });
    this.detectChanges();
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.showError(_ti('public.message.data_invalid'));
      return;
    }

    try {
      const formValue = this.form.value;
      this.contactData.name = _.trim(formValue.name);
      this.contactData.phoneNumber = _.trim(formValue.phoneNumber);
      this.contactData.email = _.trim(formValue.email);

      await this.crmContactService.update(this.contactData);
      const msgKey = this.isEdit ? 'public.message.update_success' : 'public.message.create_success';
      this.toastService.showSuccess(_ti(msgKey));
      this.dismiss(null, false);
    } catch (error) {
      const errorMSG = this.isEdit ? _ti('public.message.update_failure') : _ti('public.message.create_failure');
      this.showErrorMessage(error, errorMSG);
    }
  }
}
